import axios, { type AxiosInstance } from 'axios';
import { attachAuthInterceptors } from '@/services/auth';

const CALENDAR_API = String(
  import.meta.env.VITE_CALENDAR_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'
).replace(/\/+$/, '');

export const PMS_AUTH_AUDIENCE = String(import.meta.env.VITE_PMS_AUTH_AUDIENCE || 'pms-api');
export const PMS_APP_URL = String(import.meta.env.VITE_PMS_APP_URL || '').replace(/\/+$/, '');

export type PmsProject = {
  id: string;
  name: string;
  description?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

export type PmsTask = {
  id: string;
  name: string;
  status?: string | null;
  priority?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  dueDate?: string | null;
  projectId?: string | null;
  project?: { id: string; name: string } | null;
};

export type PmsProjectsResponse = {
  data?: PmsProject[];
  items?: PmsProject[];
  projects?: PmsProject[];
  totalPages?: number;
  currentPage?: number;
};

export type PmsTasksResponse = {
  data?: PmsTask[];
  items?: PmsTask[];
  tasks?: PmsTask[];
  totalPages?: number;
  currentPage?: number;
};

function pickArray<T>(payload: unknown, keys: string[]): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (!payload || typeof payload !== 'object') return [];
  const rec = payload as Record<string, unknown>;
  for (const key of keys) {
    if (Array.isArray(rec[key])) return rec[key] as T[];
  }
  return [];
}

function unwrapCollection<T>(payload: unknown, keys: string[]): T[] {
  const direct = pickArray<T>(payload, keys);
  if (direct.length) return direct;
  if (!payload || typeof payload !== 'object') return [];
  const rec = payload as Record<string, unknown>;
  for (const nested of ['data', 'body', 'result']) {
    const items = pickArray<T>(rec[nested], keys);
    if (items.length) return items;
  }
  return [];
}

function readPagination(payload: unknown): { currentPage: number; totalPages: number } {
  if (!payload || typeof payload !== 'object') return { currentPage: 1, totalPages: 1 };
  const rec = payload as Record<string, unknown>;
  const nested = (['body', 'data', 'result']
    .map((k) => rec[k])
    .find((v) => v && typeof v === 'object') ?? {}) as Record<string, unknown>;
  const currentPage = Number(rec.currentPage ?? rec.page ?? nested.currentPage ?? nested.page ?? 1);
  const totalPages = Number(rec.totalPages ?? rec.pages ?? nested.totalPages ?? nested.pages ?? 1);
  return {
    currentPage: Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1,
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
  };
}

function createPmsClient(): AxiosInstance {
  const client = axios.create({
    baseURL: `${CALENDAR_API}/api/v1/integrations/pms`,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });

  // Token is read from cache on request; refresh is called ONLY on 401 response.
  attachAuthInterceptors(client, { audience: PMS_AUTH_AUDIENCE });

  return client;
}

export const pmsApi = createPmsClient();

export async function fetchPmsProjects(params?: {
  search?: string;
  from?: string;
  to?: string;
}): Promise<PmsProject[]> {
  const limit = 50;
  const all: PmsProject[] = [];
  let page = 1;
  let totalPages = 1;
  let rounds = 0;

  do {
    const query: Record<string, string | number> = { limit, page };
    if (params?.search?.trim()) query.search = params.search.trim();
    if (params?.from?.trim()) query.from = params.from.trim();
    if (params?.to?.trim()) query.to = params.to.trim();

    const res = await pmsApi.get<PmsProjectsResponse>('/projects', { params: query });
    all.push(...unwrapCollection<PmsProject>(res.data, ['projects', 'data', 'items']));
    const p = readPagination(res.data);
    page = p.currentPage + 1;
    totalPages = p.totalPages;
    rounds += 1;
  } while (page <= totalPages && rounds < 50);

  return all;
}

export async function fetchPmsTasks(params?: {
  projectId?: string;
  search?: string;
  status?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
}): Promise<PmsTask[]> {
  const limit = 100;
  const all: PmsTask[] = [];
  let page = 1;
  let totalPages = 1;
  let rounds = 0;

  do {
    const query: Record<string, string | number> = { limit, page };
    if (params?.search?.trim()) query.search = params.search.trim();
    if (params?.status?.trim()) query.status = params.status.trim();
    if (params?.dueDateFrom?.trim()) query.dueDateFrom = params.dueDateFrom.trim();
    if (params?.dueDateTo?.trim()) query.dueDateTo = params.dueDateTo.trim();

    const res = params?.projectId
      ? await pmsApi.get<PmsTasksResponse>(`/projects/${params.projectId}/tasks`, { params: query })
      : await pmsApi.get<PmsTasksResponse>('/tasks', { params: query });

    all.push(...unwrapCollection<PmsTask>(res.data, ['tasks', 'data', 'items']));
    const p = readPagination(res.data);
    page = p.currentPage + 1;
    totalPages = p.totalPages;
    rounds += 1;
  } while (page <= totalPages && rounds < 50);

  return all;
}
